import {
  Col,
  Row,
  Form,
  Button,
  Collapse,
  ListGroup,
  FormControl
} from "react-bootstrap";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useOutletContext, useParams } from "react-router";
import { QueryBuilder, formatQuery } from "react-querybuilder";
import { QueryBuilderBootstrap } from "@react-querybuilder/bootstrap";
import ExternalLink from "./external-link";
import SaveFiltersButton from "./save-filters-btn";
import { VerticalDivider } from "../common-styling";
import PreventReload from "../funnel-form/prevent-reload";
import TextField from "../reusable-form-fields/text-field";
import { COUNTRY_GERMANY, FUNNEL_TYPE_FILTER } from "../strings";
import { updateFunnelFilters } from "../../services/api-declaration";
import { showError, showSuccess } from "../../services/toast-service";
import ValidationMessage from "../validation-message/validation-message";
import { FunnelFiltersFormSchema } from "../../services/validation-schema";
import { useTranslation } from "react-i18next";
import "./funnel-filters.scss";

const validator = (r) => !!r.value;
const fields = [
  {
    name: "postalCode",
    label: "Postal Code",
    valueEditorType: "number",
    comparator: (f) => f.datatype === "number",
    datatype: "number",
    validator,
    operators: [
      { name: "=", label: "Exactly matches" },
      { name: "<", label: "Smaller than" },
      { name: ">", label: "Greater than" },
      { name: ">=", label: "Greater than equal to" },
      { name: "<=", label: "Smaller than equal to" },
      { name: "beginsWith", label: "Begins with" }
    ]
  }
  // {
  //     name: "email",
  //     label: "Email",
  //     validator: ({ value }) => /^[^@]+@[^@]+/.test(value),
  // }
];

const STATUS_ENUM = {
  ACTIVE: "ACTIVE",
  IN_ACTIVE: "IN-ACTIVE"
};

const initialQuery = {
  combinator: "and",
  rules: []
};

const MutliPCContainer = styled.div`
  background-color: white;
  border: 1px solid #959ba1;
  border-radius: 6px;
`;
const ExternalLinksContainer = styled.div`
  width: 100%;
  display: flex;
  padding-bottom: 10px;
`;
const MultiPCTextArea = styled(FormControl)`
  border: 0;
  margin-top: 2px;
  height: 100%;
  width: 98%;
  resize: none;
  border-radius: 0;
  &:focus {
    box-shadow: none;
  }
  &:focus-visible {
    outline: none;
  }
`;
const TextBtn = styled.h6`
  cursor: pointer;
`;
const GroupedList = styled(ListGroup)`
  max-height: 550px;
  overflow-y: scroll;
`;
const GroupedListHeader = styled.div`
  z-index: 1019 !important;
`;

const FunnelFilters = () => {
  const { t } = useTranslation();
  const params = useParams();
  const { funnel, setFunnel } = useOutletContext();

  const generateFilterHeading = () =>
    funnel.filters?.heading
      ? funnel.filters?.heading
      : funnel.general.funnelType === FUNNEL_TYPE_FILTER.LEAD
        ? "Anfrage außerhalb des Einzugsgebietes"
        : funnel.general.funnelType === FUNNEL_TYPE_FILTER.EMPLOYEE
          ? "Bewerbung außerhalb des Einzugsgebietes"
          : "";
  const generateFilterMessage = () =>
    funnel.filters?.message
      ? funnel.filters?.message
      : funnel.general.funnelType === FUNNEL_TYPE_FILTER.LEAD
        ? `Wir danken Ihnen herzlich für Ihr Interesse.\n\nLeider befindet sich Ihr Wohnort außerhalb des von uns definierten Einzugsgebiets.\nAus logistischen Gründen können wir Ihre Anfrage daher aktuell nicht berücksichtigen.\n\nMit freundlichen Grüßen`
        : funnel.general.funnelType === FUNNEL_TYPE_FILTER.EMPLOYEE
          ? `Vielen Dank für Dein Interesse an einer Stelle in unserem Unternehmen. Leider liegt Dein Wohnort außerhalb des von uns definierten Einzugsgebiets. Aus logistischen Gründen können wir Deine Bewerbung daher aktuell nicht berücksichtigen.\n\nWir schätzen Dein Engagement und wünschen Dir weiterhin viel Erfolg bei Deiner Jobsuche.\n\nHerzliche Grüße`
          : "";

  const initialValues = {
    heading: generateFilterHeading(),
    message: generateFilterMessage(),
    multiPostalCode: funnel.filters?.multiPostalCode || "",
    multiPostalCodes: funnel.filters?.multiPostalCodes || []
  };

  const methods = useForm({
    resolver: yupResolver(FunnelFiltersFormSchema),
    defaultValues: initialValues
  });
  const {
    getValues,
    watch,
    clearErrors,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = methods;

  const [query, setQuery] = useState(initialQuery);
  const [multiPostalCodes, setMultiPostalCodes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    clearErrors();
    const queryData = query || initialQuery;
    setIsSubmitting(true);
    const version = funnel?.version;
    try {
      const body = {
        version,
        filters: {
          data: queryData,
          query: formatQuery(queryData, "mongodb"),
          logic: formatQuery(queryData, { format: "cel", parseNumbers: true }),
          multiPostalCodes: multiPostalCodes
            .filter((d) => d.status === STATUS_ENUM.ACTIVE)
            .map((d) => d.name),
          message: getValues("message"),
          heading: getValues("heading")
        }
      };
      const response = await updateFunnelFilters(params.id, body);
      setFunnel({ ...funnel, filters: body.filters });
      showSuccess(response.data.message || `Funnel updated successfully!`);
      reset({}); // resetting formState of isDirty for allow to navigate after save
    } catch (error) {
      showError(
        error.response.data.message || t("toastMessage.updateFunnelFilters")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const addLocation = () => {
    const data = getValues("multiPostalCode");
    let transformedData = data
      .replace(/[^\d\n]/g, "")
      .split("\n")
      .filter((d) => d.length);
    transformedData = transformedData.map((d) => ({
      name: d,
      status: STATUS_ENUM.ACTIVE
    }));
    const collection = [...multiPostalCodes, ...transformedData];
    setMultiPostalCodes(collection);
    updateMultiPostalCodesFormValue(collection);
  };

  const togglePostalCode = (postalCode) => {
    setMultiPostalCodes((postalCodes) => {
      const newData = [
        ...postalCodes.map((p) => {
          if (p.name === postalCode.name) {
            p.status =
              p.status === STATUS_ENUM.ACTIVE
                ? STATUS_ENUM.IN_ACTIVE
                : STATUS_ENUM.ACTIVE;
          }
          return p;
        })
      ];
      return newData;
    });
  };

  const updateMultiPostalCodesFormValue = (data) =>
    setValue("multiPostalCodes", data, { shouldDirty: true });

  const handleClearAll = () => {
    setMultiPostalCodes([]);
    updateMultiPostalCodesFormValue([]);
  };

  useEffect(() => {
    if (funnel) {
      setQuery(funnel.filters?.data);
      const transformedData = funnel.filters?.multiPostalCodes.map((d) => ({
        name: d,
        status: STATUS_ENUM.ACTIVE
      }));
      setMultiPostalCodes(transformedData || []);
    }
  }, [funnel]);

  return (
    <>
      <QueryBuilderBootstrap>
        {/* <h5 className="my-4">Filters</h5> */}
        <div className="mt-4">
          <QueryBuilder
            showCombinatorsBetweenRules
            controlClassnames={{
              queryBuilder: "queryBuilder-branches",
              addRule: "as"
            }}
            fields={fields}
            query={query || initialQuery}
            onQueryChange={(q) => setQuery(q)}
            translations={{
              addGroup: { label: t("addNewGroup"), title: "" },
              addRule: {
                label: t("addNewCondition"),
                title: ""
              }
            }}
          />
        </div>
      </QueryBuilderBootstrap>
      <div className="my-4">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <ExternalLinksContainer>
              <ExternalLink
                country={COUNTRY_GERMANY}
                href="https://www.freemaptools.com/find-german-postcodes-inside-radius.htm"
              />
              <ExternalLink
                country="switzerland"
                href="https://www.freemaptools.com/find-switzerland-postal-codes-inside-radius.htm"
              />
              <ExternalLink
                country="austria"
                href="https://www.freemaptools.com/find-austrian-postcodes-inside-radius.htm"
              />
            </ExternalLinksContainer>
          </div>
          <SaveFiltersButton
            size="sm"
            isSubmitting={isSubmitting}
            onClick={() =>
              !query?.rules.length ? handleSave() : handleSubmit(handleSave)()
            }
          />
        </div>
        <MutliPCContainer className="px-0">
          <>
            <Row>
              <Col xs={12} sm={3} className="position-relative">
                <div className="p-3">
                  <h5>{t("funnelFilters.multiPCHeading")}</h5>
                  <p>{t("funnelFilters.multiPCCaption")}</p>
                  <VerticalDivider />
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={addLocation}
                    disabled={!watch("multiPostalCode")}
                  >
                    {t("funnelFilters.addLocations")}
                  </Button>
                </div>
              </Col>
              <Col xs={12} sm={9}>
                <Form.Group className="mb-3" controlId={"multi-postal-code"}>
                  <MultiPCTextArea
                    {...register("multiPostalCode")}
                    rows={5}
                    as="textarea"
                    placeholder={t("funnelFilters.multiPCPlaceHolder")}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        </MutliPCContainer>
      </div>
      <Collapse in={multiPostalCodes.length > 0}>
        <GroupedList>
          <GroupedListHeader className="sticky-top bg-primary rounded-top px-4 pt-1 d-flex justify-content-between">
            <h6 className="text-white">{`${t(
              "funnelFilters.selectedCodesHeading"
            )} (${
              multiPostalCodes.filter((d) => d.status === STATUS_ENUM.ACTIVE)
                .length
            })`}</h6>
            <TextBtn className="text-white" onClick={handleClearAll}>
              {t("funnelFilters.clearAll")}
            </TextBtn>
          </GroupedListHeader>
          {multiPostalCodes.map((postalCode, idx) => (
            <ListGroup.Item key={idx} className="px-2 py-1">
              <Form.Check
                className="mb-0"
                type={"checkbox"}
                checked={postalCode.status === STATUS_ENUM.ACTIVE}
                onChange={() => togglePostalCode(postalCode)}
                label={postalCode.name}
                id={`default-checkbox`}
              />
            </ListGroup.Item>
          ))}
        </GroupedList>
      </Collapse>
      <FormProvider {...methods}>
        <PreventReload />
        <Form className="my-4">
          <div className="px-0">
            <Row>
              <Col>
                <TextField
                  label={t("common.headline")}
                  placeholder={t("enterHeading")}
                  type="text"
                  name="heading"
                  errors={errors?.heading}
                  register={register}
                  noSpacing
                />
                <Form.Group className="my-2 mb-0" controlId={"Message"}>
                  <Form.Label className={errors.message && "text-danger"}>
                    {t("common.message")}
                    {errors.message && "*"}
                  </Form.Label>

                  <Form.Control
                    className={errors?.message ? "border-danger" : ""}
                    rows="4"
                    as="textarea"
                    placeholder={t("msgPlaceholder")}
                    {...register("message")}
                  />
                  <ValidationMessage error={errors?.message?.message} />
                </Form.Group>
              </Col>
            </Row>
          </div>
          <hr />
          <SaveFiltersButton
            isSubmitting={isSubmitting}
            onClick={() =>
              !query?.rules.length ? handleSave() : handleSubmit(handleSave)()
            }
          />
        </Form>
      </FormProvider>
    </>
  );
};

export default FunnelFilters;
